import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ArrowSquareOut, DownloadSimple, CircleNotch } from "@phosphor-icons/react";

// Previsualiza un PDF (ruta de la API) en un iframe sin descargarlo.
export default function PdfPreviewDialog({ open, onOpenChange, url, title, filename }) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !url) return;
    let current = null;
    setBlobUrl(null); setError(null);
    api.get(url, { responseType: "blob" })
      .then((res) => {
        current = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
        setBlobUrl(current);
      })
      .catch(async (err) => {
        let msg = "No se pudo generar el PDF";
        try { msg = JSON.parse(await err?.response?.data?.text())?.detail || msg; } catch { /* ignore */ }
        setError(typeof msg === "string" ? msg : "No se pudo generar el PDF");
      });
    return () => { if (current) window.URL.revokeObjectURL(current); };
  }, [open, url]);

  const descargar = () => {
    if (!blobUrl) return;
    const a = document.createElement("a");
    a.href = blobUrl; a.download = filename || "comprobante.pdf";
    document.body.appendChild(a); a.click(); a.remove();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="pdf-preview-dialog" className="max-w-5xl w-[95vw] h-[90vh] flex flex-col p-0 gap-0 rounded-sm">
        <DialogHeader className="px-4 py-3 border-b border-[#E5E5E5] flex-row items-center justify-between space-y-0 pr-12">
          <div>
            <DialogTitle className="text-[14px] font-mono">{title || "Comprobante"}</DialogTitle>
            <DialogDescription className="text-[11px]">Vista previa · no se descarga hasta que lo pidas</DialogDescription>
          </div>
          <div className="flex items-center gap-1">
            <Button data-testid="pdf-preview-open-tab" size="sm" variant="outline" className="rounded-sm h-7 text-[11px]"
              disabled={!blobUrl} onClick={() => window.open(blobUrl, "_blank")}>
              <ArrowSquareOut size={13} className="mr-1" /> Abrir en pestaña
            </Button>
            <Button data-testid="pdf-preview-download" size="sm" variant="outline" className="rounded-sm h-7 text-[11px]"
              disabled={!blobUrl} onClick={descargar}>
              <DownloadSimple size={13} className="mr-1" /> Descargar
            </Button>
          </div>
        </DialogHeader>
        <div className="flex-1 bg-neutral-100 min-h-0">
          {error && <div data-testid="pdf-preview-error" className="h-full flex items-center justify-center text-[13px] text-red-700">{error}</div>}
          {!error && !blobUrl && (
            <div className="h-full flex items-center justify-center text-[12px] text-neutral-500 gap-2">
              <CircleNotch size={16} className="animate-spin" /> Generando vista previa…
            </div>
          )}
          {blobUrl && <iframe data-testid="pdf-preview-frame" title={title || "PDF"} src={`${blobUrl}#toolbar=0`} className="w-full h-full border-0" />}
        </div>
      </DialogContent>
    </Dialog>
  );
}
